import React, {
  PropsWithChildren,
  useEffect,
  useReducer,
  useState,
} from "react";
import SwiperCore, {
  Navigation,
  Pagination,
  Keyboard,
  Thumbs,
  EffectFade,
  Autoplay,
  Zoom,
} from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import ChevronDown from "assets/icons/chevron-down.svg";
import { useForceUpdate } from "helpers/hooks";
import { cn } from "helpers/style";
import styles from "./Slider.module.scss";

SwiperCore.use([
  Zoom,
  Navigation,
  Pagination,
  Keyboard,
  Thumbs,
  EffectFade,
  Autoplay,
]);

const allowSlideInitialState = {
  prev: false,
  next: true,
};

type AllowSlideStateType = typeof allowSlideInitialState;
type AllowSlideAction =
  | {
      type: "start" | "end" | "reset";
    }
  | {
      type: "edge";
      isBeginning: boolean;
      isEnd: boolean;
    };

function reachReducer(
  state: AllowSlideStateType,
  action: AllowSlideAction
): AllowSlideStateType {
  switch (action.type) {
    case "end":
      return { prev: true, next: false };
    case "start":
      return { prev: false, next: true };
    case "edge":
      return { prev: !action.isBeginning, next: !action.isEnd };
    case "reset":
      return allowSlideInitialState;
    default:
      return state;
  }
}

export type SliderProps = Swiper;

export type Props = PropsWithChildren<
  Partial<
    {
      customNav: boolean;
      className: string;
      classNameSlide: string;
      classNameBtnPrev: string;
      classNameBtnNext: string;
      onSliderInit: (swiper: SwiperCore) => void;
    } & SliderProps
  >
>;

const Slider: React.FC<Props> = ({
  children,
  loop,
  /* todo: refactor default with false */
  customNav = true,
  className,
  classNameSlide,
  onSliderInit,
  classNameBtnPrev = null,
  classNameBtnNext = null,
  ...props
}: Props) => {
  const [swiper, setSwiper] = useState(null); // if there are problems with performance refactor with useRef
  const forceUpdate = useForceUpdate();
  const [allowSlide, allowSlideDispatch] = useReducer<typeof reachReducer>(
    reachReducer,
    allowSlideInitialState,
    undefined // maybe use prop for control initial state with initialize
  );
  const items = Array.isArray(children) ? children : [children];
  const prevSlideAllowed = loop || allowSlide.prev;
  const nextSlideAllowed = loop || allowSlide.next;
  const prevDisabled = !prevSlideAllowed || !swiper?.allowSlidePrev;
  const nextDisabled = !nextSlideAllowed || !swiper?.allowSlideNext;

  function onSwiper(swiper) {
    setSwiper(swiper);
    onSliderInit && onSliderInit(swiper);
  }

  function slidePrev() {
    swiper && swiper.slidePrev();
  }

  function slideNext() {
    swiper && swiper.slideNext();
  }

  useEffect(() => {
    if (swiper) {
      swiper.slideTo(props.initialSlide || 0);
      swiper.update(); /* some type of re-init, affect watchOverflow & allowSlide* in swiper instance */
      allowSlideDispatch({
        type: "edge",
        isBeginning: swiper.isBeginning,
        isEnd: swiper.isEnd,
      });
      forceUpdate(); /* re-render for update prevDisabled & nextDisabled */
    }
  }, [children]);

  return (
    <div className={cn([styles.slider, className])}>
      {customNav && (
        <button
          className={cn([
            styles.sliderControl,
            styles.sliderControlLeft,
            prevDisabled && styles.sliderControlDisabled,
            classNameBtnPrev,
          ])}
          aria-label="предыдущий"
          onClick={slidePrev}
          disabled={prevDisabled}
          type="button"
        >
          <ChevronDown />
        </button>
      )}
      <Swiper
        onSwiper={onSwiper}
        onReachBeginning={() => allowSlideDispatch({ type: "start" })}
        onReachEnd={() => allowSlideDispatch({ type: "end" })}
        onFromEdge={swiper => {
          items.length > 2 &&
            allowSlideDispatch({
              type: "edge",
              isBeginning: swiper.isBeginning,
              isEnd: swiper.isEnd,
            });
        }}
        loop={loop}
        watchOverflow
        preloadImages={false}
        {...props}
      >
        {items.map((item, i) => (
          <SwiperSlide key={i} className={classNameSlide || null}>
            {item}
          </SwiperSlide>
        ))}
      </Swiper>
      {customNav && (
        <button
          className={cn([
            styles.sliderControl,
            styles.sliderControlRight,
            nextDisabled && styles.sliderControlDisabled,
            classNameBtnNext,
          ])}
          aria-label="следующий"
          onClick={slideNext}
          disabled={nextDisabled}
          type="button"
        >
          <ChevronDown />
        </button>
      )}
    </div>
  );
};

export default Slider;
